import React from "react";

import { useTranslation } from "lib/i18n/use-translation";

import { Card, NotEnoughData } from "../engagement/EngagementTab";
import type { EngagementView } from "../engagement/engagement-view";
import engagementStyles from "../engagement/engagement.module.scss";
import { segments } from "./donut";
import styles from "./engagement-redesign.module.scss";

const K = "admin:companyDashboard.engagement";

// The prototype's four colours plus a deeper fifth, given out by rank as
// Platform orders the topics; any past the fifth reuse it.
const COLOURS = ["#ff875e", "#e07848", "#ffb596", "#ffd8c8", "#b85a30"];
const colour = (index: number) => COLOURS[Math.min(index, COLOURS.length - 1)];

// A circle of this radius has a circumference of 100, so a segment's dash
// lengths are its percentages.
const RADIUS = 15.915;

// Safari drops a list's role once its markers are styled away, so the legend
// states it again.
const LIST = "list";

const Topics = ({
  topics,
  day,
}: {
  topics: EngagementView["topics"];
  day: string;
}) => {
  const { t } = useTranslation(["admin"]);
  const name = (topic: string) =>
    t(`${K}.topics.names.${topic}`, { defaultValue: topic });

  // Says what the split is drawn from whenever part of the engagement has no
  // topic, including when none of it does.
  const coverage = topics && !topics.complete && (
    <p className={engagementStyles["footnote"]}>
      {t(`${K}.topics.coverage`, {
        counted: topics.counted,
        total: topics.total,
      })}
    </p>
  );

  return (
    <Card
      title={t(`${K}.topics.title`)}
      description={t(`${K}.topics.description`)}
    >
      {topics === null ? (
        <NotEnoughData />
      ) : topics.rows.length === 0 ? (
        <>
          <p className={engagementStyles["not-enough"]}>
            {t(`${K}.topics.none`)}
          </p>
          {coverage}
        </>
      ) : (
        <>
          <svg
            className={styles["donut"]}
            viewBox="0 0 42 42"
            aria-hidden="true"
            focusable="false"
          >
            {/* Starts at three o'clock and runs anticlockwise. */}
            <g transform="translate(0 42) scale(1 -1)">
              {segments(topics.rows.map((row) => row.fraction)).map(
                ({ start, length }, index) => (
                  <circle
                    key={topics.rows[index].topic}
                    className={styles["donut-segment"]}
                    cx="21"
                    cy="21"
                    r={RADIUS}
                    style={{ stroke: colour(index) }}
                    strokeDasharray={`${length} ${100 - length}`}
                    strokeDashoffset={100 - start}
                  />
                )
              )}
            </g>
          </svg>
          <ul
            className={styles["legend"]}
            role={LIST}
            aria-label={t(`${K}.topics.legend`)}
          >
            {topics.rows.map((row, index) => (
              <li key={row.topic} className={styles["legend-item"]}>
                <span
                  className={styles["legend-swatch"]}
                  style={{ backgroundColor: colour(index) }}
                  aria-hidden="true"
                />
                <span className={styles["legend-text"]}>
                  <span className={styles["legend-name"]}>
                    {name(row.topic)}
                  </span>
                  <span className={styles["legend-share"]}>{row.share}</span>
                </span>
              </li>
            ))}
          </ul>
          {coverage}
        </>
      )}
      <p className={engagementStyles["footnote"]}>
        {t(`${K}.quarter.dataThrough`, { date: day })}
      </p>
    </Card>
  );
};

export default Topics;
